import { trackPromise } from 'react-promise-tracker';

import axios from 'axios';
import isEmpty from 'lodash-es/isEmpty';

import { getEcpErrorMessage } from '@ecp/common/src/utils/errorUtils';
import { clearAccessTokens, getAccessToken, saveAccessTokens } from '@ecp/common/src/utils/utils';
import pathnameConverter from '@ecp/common/src/utils/pathnameConverter';

import { showExternalAlertDialog } from '@fo-hooks/common/useModal';
import authApi from '@fo-apis/common/authApi';
import commonApi from '@fo-apis/common/commonApi';
const BASE_URL = process.env.REACT_APP_BASE_URL;
export const DEFAULT_VALID_STATUS_CODE = [200, 201, 202, 204];
const DEFAULT_ERROR_STATUS_CODE = [403, 404, 500];
const AUTH_ERROR_STATUS_CODE = [401];

const headers = {
  Accept: 'application/json',
};

const axiosClient = axios.create({
  baseURL: BASE_URL,
  headers,
  withCredentials: true,
});

const axiosSkipErrorClient = axios.create({
  baseURL: BASE_URL,
  headers,
  withCredentials: true,
});

export const axiosRawClient = axios.create({
  baseURL: BASE_URL,
  headers,
  withCredentials: true,
});

const requestInterceptor = (config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

const moveLogoutPage = async () => {
  try {
    await authApi.logout();
  } catch (e) {
    console.error('Fail to clear auth session.', e);
  }
  clearAccessTokens();

  const paramString = pathnameConverter.toParamString(window.location.pathname);
  if (!isEmpty(paramString)) {
    window.location.replace(`/logout?${paramString}`);
    return;
  }
  window.location.replace('/logout');
};

const GUEST_MOVE_PAGE_KEY = 'guest-move-page';
const moveCurrentPage = () => {
  clearAccessTokens();
  
  if(sessionStorage.getItem(GUEST_MOVE_PAGE_KEY))
  return;

  sessionStorage.setItem(GUEST_MOVE_PAGE_KEY, true);

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(currentUrl || '/');
};

const sendUnknownError = async (error) => {
  await commonApi.sendUnknownError(JSON.stringify(error));
};

const moveErrorPage = (errorCode) => {
  window.location.replace(`/error/${errorCode}`);
};

const responseErrorInterceptor = async (error) => {
  if (DEFAULT_ERROR_STATUS_CODE.includes(error.response?.status)) {
    // 403, 404, 500
    moveErrorPage(error.response?.status);
  } else if (AUTH_ERROR_STATUS_CODE.includes(error.response?.status)) {
    //401
    if (error.response?.data?.errorCode === 'TOKEN_EXPIRED') {
      try {
        const response = await authApi.reissue();
        const { data: { result: { accessToken: newAccessToken } } = {} } = response;
        if (newAccessToken) {
          saveAccessTokens(newAccessToken);
        } else {
          await moveLogoutPage();
          return Promise.reject(error);
        }
        //Retry Origin Request.
        const originRequest = error.config;
        originRequest.headers = originRequest.headers || {};
        originRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        //Resolve Origin Request.
        const originalRes = await axiosClient(originRequest);
        return Promise.resolve(originalRes);
      } catch (e) {
        const { result } = await commonApi.getSiteInformation();

        // 비회원일 경우 세션만료 alert 메시지 안띄우고 바로 게스트로그인을 한다.
        if (result !== null && result.noMemberUseYn === 'Y') {
          moveCurrentPage();
        } else {
          if (e?.response?.data?.errorCode === 'TOKEN_EXPIRED') {
            showExternalAlertDialog('세션만료되어 로그아웃되었습니다', async () => {
              moveLogoutPage();
            });
          } else {
            showExternalAlertDialog('알 수 없는 오류로 로그아웃되었습니다', async () => {
              await sendUnknownError(e);
              moveLogoutPage();
            });
          }
        }
      }
    } else if (error.response?.data?.errorCode === 'INVALID_TOKEN') {
      const { result } = await commonApi.getSiteInformation();
      if (result !== null && result.noMemberUseYn === 'Y') {
        moveCurrentPage();
      } else {
        showExternalAlertDialog('세션만료되어 로그아웃되었습니다', async () => {
          moveLogoutPage();
        });
      }
    }
  } else if (error.response?.data?.errorType === 'ECP_EXCEPTION') {
    showExternalAlertDialog(getEcpErrorMessage(error), () => {});
  } else {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    } else {
      return window.location.replace('/serverError.html');
    }
  }
  return Promise.reject(error);
};

axiosClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));
axiosClient.interceptors.response.use((response) => response.data, responseErrorInterceptor);

axiosSkipErrorClient.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));

const validStatusConfig = (validStatusList) => ({
  validateStatus: (status) => {
    if (validStatusList.length < 1) {
      return true;
    }
    return validStatusList.includes(status);
  },
});

export const getRequest = (options, skip = {}) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
      ...options,
      method: 'GET',
      url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
    })
    : axiosClient({
      ...options,
      method: 'GET',
      url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
    });
  return skipSpinner ? client : trackPromise(client);
};

export const postRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
      ...options,
      ...validStatusConfig(validStatusList),
      url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
      method: 'POST',
    })
    : axiosClient({
      ...options,
      ...validStatusConfig(validStatusList),
      url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
      method: 'POST',
    });
  return skipSpinner ? client : trackPromise(client);
};

export const putRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'PUT',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'PUT',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const patchRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'PATCH',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'PATCH',
      });
  return skipSpinner ? client : trackPromise(client);
};

export const deleteRequest = (options, skip = {}, validStatusList = DEFAULT_VALID_STATUS_CODE) => {
  const { skipError = false, skipSpinner = false } = skip;
  const client = skipError
    ? axiosSkipErrorClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'DELETE',
      })
    : axiosClient({
        ...options,
        ...validStatusConfig(validStatusList),
        url: `${options.url.startsWith('/')? '' : '/'}${options.url}`,
        method: 'DELETE',
      });
  return skipSpinner ? client : trackPromise(client);
};
